import { AuditForm } from "./AuditForm";

/**
 * Compute remaining-capacity slots for the current calendar quarter,
 * based on our actual throughput: 1 site refonte / 5 jours ouvrés
 * (weekdays only). Honest baseline — no fake scarcity, no DGCCRF risk.
 * Number drops naturally as the quarter progresses.
 */
function remainingSlotsThisQuarter(): { count: number; quarter: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3); // 0..3
  const quarterEndExclusive = new Date(now.getFullYear(), q * 3 + 3, 1);
  let workingDays = 0;
  const cursor = new Date(now);
  while (cursor < quarterEndExclusive) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) workingDays++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return {
    count: Math.floor(workingDays / 5),
    quarter: `Q${q + 1} ${now.getFullYear()}`,
  };
}

export function Hero() {
  const { count, quarter } = remainingSlotsThisQuarter();
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center px-6 py-24 bg-slate-950 overflow-hidden"
    >
      {/* Background gradient — Levier IA blue/indigo */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/30 via-slate-950 to-slate-950" />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-40" />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Eyebrow — scarcity + live badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 text-sm font-medium text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
          </span>
          {/* Capacity computed live from working days remaining this
              quarter, at our delivery throughput of 1 site / 5 jours
              ouvrés. The formula stays internal; the eyebrow just
              surfaces the resulting headroom. Honest baseline — no
              fake scarcity claim. */}
          Conseil IA · {count} sites max {quarter}
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight">
          Votre site est invisible{" "}
          <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">
            dans ChatGPT.
          </span>
          <br />
          <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-300 font-bold">
            Voici pourquoi — en 2 minutes.
          </span>
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Notre agent autonome{" "}
          <code className="text-blue-300 font-mono text-base bg-slate-900/60 px-1.5 py-0.5 rounded">
            audit@agents.levier-ia.fr
          </code>{" "}
          analyse votre site sous <strong className="text-white">2 à 3 minutes</strong> et vous envoie un rapport actionnable.{" "}
          <span className="text-slate-300">SEO classique + GEO (ChatGPT, Perplexity, Gemini), schema.org, performance.</span>
        </p>

        {/* Conversion-critical: inline form, no extra click */}
        <div className="mt-10">
          <AuditForm variant="hero" id="audit-form-hero" source="landing-hero" />
        </div>

        {/* Trust signals — sovereignty + zero-friction */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            0 € · 0 RDV · sans engagement
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Rapport détaillé par email
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Hébergement souverain (OVHcloud)
          </div>
        </div>
      </div>
    </section>
  );
}
